/**
 * Módulo centralizado para formatação de logs
 * Aplica destaque visual para timestamps, [ERROR] e [DEBUG]
 * Remove logs aninhados e linhas "Resposta da API"
 */

/**
 * Formata logs aplicando destaque visual e limpeza
 * @param {string} texto - Texto do log original
 * @param {boolean} debug - Se deve mostrar logs de debug (padrão: false)
 * @returns {string} - Texto formatado com HTML
 */
export function formatarLog(texto, debug = false) {
  if (!texto) return '';
  
  if (debug) {
    console.log('[formatterlog] Texto original:', texto.substring(0, 200) + '...');
  }
  
  // Limpar logs aninhados/duplicados que podem vir da API
  let textoLimpo = limparLogsAninhados(texto, debug);
  
  // Filtrar linhas que contêm "Resposta da API"
  textoLimpo = filtrarLinhasRespostaAPI(textoLimpo, debug);
  
  // Aplicar destaque visual
  const resultado = aplicarDestaqueVisual(textoLimpo, debug);
  
  if (debug) {
    console.log('[formatterlog] Resultado final:', resultado.substring(0, 200) + '...');
  }
  
  return resultado;
}

/**
 * Remove logs aninhados/duplicados que podem vir da API
 * @param {string} texto - Texto original
 * @param {boolean} debug - Se deve mostrar logs de debug
 * @returns {string} - Texto limpo
 */
function limparLogsAninhados(texto, debug = false) {
  let textoLimpo = texto;
  
  // Procurar por padrões de logs aninhados em respostas da API
  const regexLogsAninhados = /"logs":"([^"]*(?:\\.[^"]*)*)"/g;
  const matches = texto.match(regexLogsAninhados);
  
  if (matches && matches.length > 0) {
    if (debug) {
      console.log('[formatterlog] Logs aninhados encontrados:', matches.length);
    }
    
    // Remover logs aninhados que são duplicatas
    matches.forEach(match => {
      const logAninhado = match.replace(/^"logs":"/, '').replace(/"$/, '');
      const logLimpo = logAninhado.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
      
      // Se o log aninhado é uma substring do log principal, remover
      if (textoLimpo.includes(logLimpo) && logLimpo.length < textoLimpo.length * 0.8) {
        if (debug) {
          console.log('[formatterlog] Removendo log aninhado duplicado');
        }
        textoLimpo = textoLimpo.replace(match, '"logs":"[LOG ANINHADO REMOVIDO]"');
      }
    });
  }
  
  return textoLimpo;
}

/**
 * Filtra linhas que contêm "Resposta da API"
 * @param {string} texto - Texto para filtrar
 * @param {boolean} debug - Se deve mostrar logs de debug
 * @returns {string} - Texto filtrado
 */
function filtrarLinhasRespostaAPI(texto, debug = false) {
  const linhas = texto.split('\n');
  const linhasFiltradas = linhas.filter(linha => {
    // Verificar se a linha contém "Resposta da API" (case insensitive)
    const contemRespostaAPI = /resposta da api/i.test(linha);
    if (contemRespostaAPI && debug) {
      console.log('[formatterlog] Removendo linha com "Resposta da API":', linha.substring(0, 100) + '...');
    }
    return !contemRespostaAPI;
  });
  
  if (debug) {
    console.log('[formatterlog] Linhas removidas:', linhas.length - linhasFiltradas.length);
  }
  
  return linhasFiltradas.join('\n');
}

/**
 * Aplica destaque visual para timestamps, [ERROR] e [DEBUG]
 * @param {string} texto - Texto para aplicar destaque
 * @param {boolean} debug - Se deve mostrar logs de debug
 * @returns {string} - Texto com destaque HTML
 */
function aplicarDestaqueVisual(texto, debug = false) {
  // Regex para encontrar timestamps no formato ISO: YYYY-MM-DDTHH:MM:SS.sssZ ou YYYY-MM-DDTHH:MM:SS
  const regexTimestamp = /\[(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z)?)\]/g;
  
  // Regex para encontrar [ERROR]
  const regexError = /\[ERROR\]/g;
  
  // Regex para encontrar [DEBUG]
  const regexDebug = /\[DEBUG\]/g;
  
  // Contar quantos elementos foram encontrados
  const timestampMatches = texto.match(regexTimestamp);
  const errorMatches = texto.match(regexError);
  const debugMatches = texto.match(regexDebug);
  
  if (debug) {
    console.log('[formatterlog] Elementos encontrados:', {
      timestamps: timestampMatches ? timestampMatches.length : 0,
      errors: errorMatches ? errorMatches.length : 0,
      debugs: debugMatches ? debugMatches.length : 0
    });
  }
  
  let resultado = texto;
  
  // Processar timestamps
  resultado = resultado.replace(regexTimestamp, (match, timestamp) => {
    try {
      if (debug) {
        console.log('[formatterlog] Processando timestamp:', match, '->', timestamp);
      }
      
      // Converter timestamp para objeto Date
      const dataOriginal = new Date(timestamp);
      
      // Verificar se a data é válida
      if (isNaN(dataOriginal.getTime())) {
        if (debug) {
          console.log('[formatterlog] Data inválida, mantendo original:', match);
        }
        return match; // Se a data for inválida, manter o timestamp original
      }
      
      // Subtrair 3 horas
      const dataAjustada = new Date(dataOriginal);
      dataAjustada.setHours(dataOriginal.getHours() - 3);
      
      // Formatar a data ajustada no mesmo formato original
      let timestampAjustado;
      if (timestamp.includes('.') && timestamp.endsWith('Z')) {
        // Formato com milissegundos e Z: YYYY-MM-DDTHH:MM:SS.sssZ
        timestampAjustado = dataAjustada.toISOString();
      } else if (timestamp.includes('.')) {
        // Formato com milissegundos: YYYY-MM-DDTHH:MM:SS.sss
        timestampAjustado = dataAjustada.toISOString().replace('Z', '');
      } else {
        // Formato simples: YYYY-MM-DDTHH:MM:SS
        timestampAjustado = dataAjustada.toISOString().split('.')[0];
      }
      
      const htmlResultado = `<span class="timestamp-highlight">[${timestampAjustado}]</span>`;
      if (debug) {
        console.log('[formatterlog] Timestamp processado:', match, '->', htmlResultado);
      }
      
      return htmlResultado;
    } catch (error) {
      console.error('[formatterlog] Erro ao ajustar timestamp:', error);
      return match; // Em caso de erro, manter o timestamp original
    }
  });
  
  // Processar [ERROR] - destacar em vermelho
  resultado = resultado.replace(regexError, (match) => {
    const htmlResultado = `<span class="error-highlight">${match}</span>`;
    if (debug) {
      console.log('[formatterlog] ERROR processado:', match, '->', htmlResultado);
    }
    return htmlResultado;
  });
  
  // Processar [DEBUG] - destacar em azul
  resultado = resultado.replace(regexDebug, (match) => {
    const htmlResultado = `<span class="debug-highlight">${match}</span>`;
    if (debug) {
      console.log('[formatterlog] DEBUG processado:', match, '->', htmlResultado);
    }
    return htmlResultado;
  });
  
  if (debug) {
    console.log('[formatterlog] Contém highlights?', {
      timestamp: resultado.includes('timestamp-highlight'),
      error: resultado.includes('error-highlight'),
      debug: resultado.includes('debug-highlight')
    });
  }
  
  return resultado;
}

/**
 * Aplica apenas a limpeza de logs (sem destaque visual)
 * Útil para casos onde não se quer HTML
 * @param {string} texto - Texto do log original
 * @param {boolean} debug - Se deve mostrar logs de debug
 * @returns {string} - Texto limpo
 */
export function limparLog(texto, debug = false) {
  if (!texto) return '';
  
  let textoLimpo = limparLogsAninhados(texto, debug);
  textoLimpo = filtrarLinhasRespostaAPI(textoLimpo, debug);
  
  return textoLimpo;
}

/**
 * Aplica apenas o destaque visual (sem limpeza)
 * @param {string} texto - Texto para aplicar destaque
 * @param {boolean} debug - Se deve mostrar logs de debug
 * @returns {string} - Texto com destaque HTML
 */
export function destacarLog(texto, debug = false) {
  if (!texto) return '';
  
  return aplicarDestaqueVisual(texto, debug);
}
